import React, {useMemo, useState} from 'react';
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faInfoCircle} from "@fortawesome/free-solid-svg-icons";

const ROLE_LABELS = {
  ADMIN: "Administrator",
  MANAGER: "Kierownik",
  EMPLOYEE: "Pracownik",
  USER: "Użytkownik",
  GUEST: "Gość",
  MINION: "Pracownik pomocniczy",
  HR: "Kadry / HR"
};

const normalizeRoleName = value => String(value || "")
  .trim()
  .toUpperCase()
  .replace(/^ROLE_/, "");

const UserRolesCard = ({state, rolesList = [], mandatoryRoleNames = [], disabled = false}) => {
  const selectedRoles = useMemo(() => {
    const currentDtos = Array.isArray(state.currentItem?.userRoleDTOS) ? state.currentItem.userRoleDTOS : [];
    const currentLegacy = Array.isArray(state.currentItem?.userRoles) ? state.currentItem.userRoles : [];
    const backupDtos = Array.isArray(state.backupItem?.userRoleDTOS) ? state.backupItem.userRoleDTOS : [];
    const backupLegacy = Array.isArray(state.backupItem?.userRoles) ? state.backupItem.userRoles : [];
    const source = currentDtos.length || currentLegacy.length
      ? [...currentDtos, ...currentLegacy]
      : [...backupDtos, ...backupLegacy];

    const unique = new Map();
    source.forEach(role => {
      if (!role) return;
      const key = role.id != null ? `id:${String(role.id)}` : `name:${normalizeRoleName(role.name)}`;
      unique.set(key, role);
    });
    return [...unique.values()];
  }, [state.currentItem?.userRoleDTOS, state.currentItem?.userRoles, state.backupItem?.userRoleDTOS, state.backupItem?.userRoles]);

  const mandatoryNames = useMemo(
    () => new Set(mandatoryRoleNames.map(normalizeRoleName)),
    [mandatoryRoleNames]
  );
  const [rolesNote, setRolesNote] = useState(false);

  const onUserRolesChange = (checked, role) => {
    if (disabled) return;
    const roleName = normalizeRoleName(role.name);
    if (!checked && mandatoryNames.has(roleName)) return;

    let nextRoles = selectedRoles.filter(item => String(item?.id) !== String(role.id));
    if (checked) {
      nextRoles = [...nextRoles, {id: role.id, name: role.name, description: role.description, systemRole: role.systemRole}];
    }

    for (const mandatoryName of mandatoryNames) {
      const mandatoryRole = rolesList.find(item => normalizeRoleName(item.name) === mandatoryName);
      if (mandatoryRole && !nextRoles.some(item => String(item.id) === String(mandatoryRole.id))) {
        nextRoles.push({
          id: mandatoryRole.id,
          name: mandatoryRole.name,
          description: mandatoryRole.description,
          systemRole: mandatoryRole.systemRole
        });
      }
    }

    state.setCurrentItem(currentItem => ({
      ...currentItem,
      userRoleDTOS: nextRoles,
      userRoles: nextRoles
    }));
    state.setItemChanged(value => !value);

    if (nextRoles.length > 0) {
      state.setCurrentFormState(current => ({...current, formSaveButtonDisabled: false, formDataValid: true}));
      setRolesNote(false);
    } else {
      state.setCurrentFormState(current => ({...current, formDataValid: false}));
      setRolesNote(true);
    }
  };

  return (
    <div className="user-role-card">
      <div className="user-role-card-header">
        <div>
          <strong>Role użytkownika</strong>
          <span>Role określają domyślny zestaw uprawnień. Indywidualne wyjątki ustawisz w sekcji uprawnień użytkownika.</span>
        </div>
      </div>
      <div className="user-role-grid" id="userRoles">
        {rolesList.map(role => {
          const roleName = normalizeRoleName(role.name);
          const mandatory = mandatoryNames.has(roleName);
          const checked = mandatory || selectedRoles.some(item => String(item?.id) === String(role.id) || normalizeRoleName(item?.name) === roleName);
          return (
            <label key={role.id} className={`user-role-option ${checked ? "selected" : ""} ${mandatory ? "mandatory" : ""}`}>
              <input
                className="form-check-input"
                type="checkbox"
                checked={checked}
                disabled={disabled || mandatory}
                onChange={event => onUserRolesChange(event.target.checked, role)}
              />
              <span className="user-role-option-copy">
                <strong>{ROLE_LABELS[roleName] || role.name}</strong>
                <small>{role.description || "Brak dodatkowego opisu roli."}</small>
                {mandatory && <em>Rola wymagana dla konta powiązanego z pracownikiem.</em>}
              </span>
            </label>
          );
        })}
      </div>
      <p className={rolesNote ? "err_info mt-2" : "offscreen"}>
        <FontAwesomeIcon icon={faInfoCircle}/>&nbsp;
        Użytkownik musi mieć przypisaną przynajmniej jedną rolę.
      </p>
    </div>
  );
};

export default UserRolesCard;

